/**
 * Prop-alias normalization for surface specs.
 *
 * Rewrites commonly hallucinated prop names (see `prop-metadata.ts`) onto
 * their canonical props, so a slightly-off spec renders correctly instead
 * of silently blanking. Two call sites:
 *
 * - agent-core `manage_surface` (write time): normalize BEFORE validation
 *   and persistence, echo the notes back to the agent as warnings so it
 *   learns the canonical names.
 * - console `AuiSurface` (render time): safety net for specs persisted
 *   before write-time normalization existed. Must run BEFORE the vfs://
 *   URI rewrite so an aliased content URL still gets rewritten.
 *
 * Pure and non-mutating: nodes are shallow-cloned only when changed.
 * No zod, no React — portable to the agent-core backend.
 */

import { componentPropAliases } from './prop-metadata';
import type { ComponentType } from './component-types';

export interface PropNormalizationNote {
  nodeId: string;
  componentType: string;
  /** The alias prop the agent sent. */
  from: string;
  /** The canonical prop it maps to. */
  to: string;
  /** false when the canonical prop was already set — the alias was dropped. */
  applied: boolean;
}

interface NormalizableNode {
  id: string;
  component: {
    type: string;
    props?: Record<string, unknown>;
  };
}

function isNormalizableNode(value: unknown): value is NormalizableNode {
  if (!value || typeof value !== 'object') return false;
  const node = value as Partial<NormalizableNode>;
  return (
    typeof node.id === 'string' &&
    !!node.component &&
    typeof node.component === 'object' &&
    typeof node.component.type === 'string'
  );
}

/**
 * Apply prop aliases to a single node. Returns the same reference when
 * nothing matched. Aliases never overwrite an explicitly-set canonical
 * prop — the alias is dropped instead (noted with `applied: false`).
 */
export function normalizeAuiNodeProps<T extends NormalizableNode>(
  node: T,
  notes?: PropNormalizationNote[],
): T {
  const aliasMap = componentPropAliases[node.component.type as ComponentType];
  const props = node.component.props;
  if (!aliasMap || !props || typeof props !== 'object') return node;

  const aliasKeys = Object.keys(props).filter((k) => aliasMap[k] !== undefined);
  if (aliasKeys.length === 0) return node;

  const next: Record<string, unknown> = { ...props };
  for (const key of aliasKeys) {
    const alias = aliasMap[key];
    const value = next[key];
    delete next[key];
    const applied = next[alias.target] === undefined;
    if (applied) {
      next[alias.target] = alias.map ? alias.map(value) : value;
    }
    notes?.push({
      nodeId: node.id,
      componentType: node.component.type,
      from: key,
      to: alias.target,
      applied,
    });
  }

  return { ...node, component: { ...node.component, props: next } };
}

export interface NormalizedSurfaceUpdates<T> {
  payload: T;
  notes: PropNormalizationNote[];
}

/**
 * Normalize a whole surface-update payload — `{ updates: [...] }` or a bare
 * array. Malformed payloads/entries pass through untouched (validation
 * reports them; normalization never throws).
 */
export function normalizeSurfaceUpdates<T>(payload: T): NormalizedSurfaceUpdates<T> {
  const notes: PropNormalizationNote[] = [];

  const normalizeArray = (updates: unknown[]): unknown[] => {
    let changed = false;
    const result = updates.map((entry) => {
      if (!isNormalizableNode(entry)) return entry;
      const normalized = normalizeAuiNodeProps(entry, notes);
      if (normalized !== entry) changed = true;
      return normalized;
    });
    return changed ? result : updates;
  };

  if (Array.isArray(payload)) {
    return { payload: normalizeArray(payload) as T, notes };
  }

  if (payload && typeof payload === 'object' && 'updates' in payload) {
    const envelope = payload as { updates: unknown };
    if (Array.isArray(envelope.updates)) {
      const normalized = normalizeArray(envelope.updates);
      if (normalized !== envelope.updates) {
        return { payload: { ...payload, updates: normalized } as T, notes };
      }
    }
  }

  return { payload, notes };
}
